<script setup>
import { ref, reactive, onMounted, onBeforeUnmount, watch } from 'vue'
import PaywallService from "@/services/PaywallService";
import { useStore } from 'vuex'
import FormInput from '../Atoms/FormInput.vue';
import IconBase from '../icon/IconBase.vue';
import IconCheckmark from '../icon/IconCheckmark.vue';

const store = useStore()

const props = defineProps({
  currentPaywall: {
    type: [Object, Function],
  }
})

const emit = defineEmits({
  updated: () => true,
})

const FIELD_MAP = { ctaText: 'cta_text', ctaFreeText: 'cta_free_text', loginText: 'login_text' }

const values = reactive({ ctaText: '', ctaFreeText: '', loginText: '' })
// Per-field inline save feedback: '' | 'saving' | 'saved' | 'error'
const status = reactive({ ctaText: '', ctaFreeText: '', loginText: '' })
const errors = reactive({ ctaText: '', ctaFreeText: '', loginText: '' })
const timers = {}

const syncFromPaywall = () => {
  values.ctaText = props.currentPaywall?.cta_text || ''
  values.ctaFreeText = props.currentPaywall?.cta_free_text || ''
  values.loginText = props.currentPaywall?.login_text || ''
}

onMounted(syncFromPaywall)
watch(() => props.currentPaywall?.id, syncFromPaywall)
onBeforeUnmount(() => Object.values(timers).forEach(clearTimeout))

const saveField = async (prop) => {
  const field = FIELD_MAP[prop]
  const value = values[prop]?.trim() ?? ''

  status[prop] = 'saving'
  errors[prop] = ''
  clearTimeout(timers[prop])
  try {
    // empty string reverts to the default label on the paywall side
    await PaywallService.updateStreamPaywallById(props.currentPaywall.id, { [field]: value })
    store.dispatch('Paywalls/setPaywallProp', { id: props.currentPaywall.id, prop: field, value: value || null })
    emit('updated')
    status[prop] = 'saved'
    timers[prop] = setTimeout(() => {
      if (status[prop] === 'saved') status[prop] = ''
    }, 2000)
  } catch (err) {
    status[prop] = 'error'
    errors[prop] = err.message || "Couldn't save"
  }
}
</script>

<template>
  <section class="bg-surface-1 rounded-xl p-4 sm:p-5">
    <div class="mb-4">
      <h3 class="text-sm font-semibold text-white mb-0.5">Call to action</h3>
      <p class="text-tiny text-surface-7 mb-0">Customize the button and link labels viewers see on your paywall. Leave a
        field blank to use the default.</p>
    </div>
    <div class="flex flex-col gap-y-4">
      <div v-for="f in [
        { key: 'ctaText', label: 'Paid access button', placeholder: 'Get access' },
        { key: 'ctaFreeText', label: 'Free access button', placeholder: 'Watch free' },
        { key: 'loginText', label: 'Returning viewer link', placeholder: 'Already purchased? Log in' },
      ]" :key="f.key">
        <div class="flex items-center justify-between gap-2 mb-1">
          <label class="font-medium text-tiny text-surface-text-1">{{ f.label }}</label>
          <!-- inline save state (replaces the toast) -->
          <span class="text-[11px] flex items-center gap-1 h-3.5">
            <template v-if="status[f.key] === 'saving'">
              <span class="text-surface-6">Saving…</span>
            </template>
            <template v-else-if="status[f.key] === 'saved'">
              <icon-base width="12" height="12" class="text-success">
                <icon-checkmark />
              </icon-base>
              <span class="text-success">Saved</span>
            </template>
            <template v-else-if="status[f.key] === 'error'">
              <span class="text-danger truncate max-w-[180px]" :title="errors[f.key]">{{ errors[f.key] }}</span>
            </template>
          </span>
        </div>
        <FormInput v-model="values[f.key]" :placeholder="f.placeholder" :maxlength="64"
          :isError="status[f.key] === 'error'" @change="saveField(f.key)" />
      </div>
    </div>
  </section>
</template>

<style scoped></style>
